/**
 * Pure classification-filter layer for the Repo Knowledge Library (Req 18.1,
 * 18.6) — ADDITIVE.
 *
 * This module holds the deterministic, side-effect-free state shape and filter
 * function the SPA uses to narrow the displayed repository list by the five
 * Library classification dimensions (Repo_Type, Lifecycle_Status, owning team,
 * Application, Product). It is intentionally kept SEPARATE from the existing
 * `FilterState` / `filterSummaries` in `lib/query.ts`: the established query
 * pipeline (search/filter/sort) is unchanged, and `types.ts` is not modified.
 *
 * Everything here is purely additive and degrades gracefully: the displayed
 * classification values come from the per-run `DashboardLibraryIndex` joined to
 * each `RepoSummary` by `repoName` + `scanId` (via `lib/query.joinLibraryEntry`).
 * When no Library document exists for the run the join map is empty, the option
 * lists are absent, and `filterByClassification` with the empty selection is a
 * no-op — so the list renders exactly as today.
 */

import type {
  DashboardLibraryIndex,
  LibraryRepoEntry,
  RepoSummary,
} from "../types";
import { joinLibraryEntry } from "./query";

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/**
 * Active classification-facet selections, one array per dimension. This mirrors
 * the attribute-dimension semantics of `FilterState` (an empty array means the
 * dimension is inactive; values are OR-ed within a dimension and dimensions are
 * AND-ed together) but is modeled separately so the existing `FilterState` type
 * and query functions are left untouched.
 *
 * Each value is the literal string a `LibraryRepoEntry` carries for that
 * dimension, including the explicit `"unknown"` marker, so a user can filter to
 * (or away from) explicitly-unknown repositories (Req 18.6).
 */
export interface ClassificationFilterState {
  repoType: string[];
  lifecycleStatus: string[];
  owningTeam: string[];
  application: string[];
  product: string[];
}

/** The empty classification selection — every dimension inactive (a no-op filter). */
export const EMPTY_CLASSIFICATION_FILTERS: ClassificationFilterState = {
  repoType: [],
  lifecycleStatus: [],
  owningTeam: [],
  application: [],
  product: [],
};

/**
 * The selectable option values per classification dimension, derived from the
 * loaded `DashboardLibraryIndex`. Each list is distinct and sorted. This is the
 * surface the FilterPanel renders facets from; it is `null` when no Library
 * document exists (graceful absence — the facets are hidden entirely).
 */
export interface ClassificationOptions {
  repoType: string[];
  lifecycleStatus: string[];
  owningTeam: string[];
  application: string[];
  product: string[];
}

/** The five classification dimensions in display order. */
export const CLASSIFICATION_DIMENSIONS = [
  "repoType",
  "lifecycleStatus",
  "owningTeam",
  "application",
  "product",
] as const;

/** One of the five classification dimensions. */
export type ClassificationDimension =
  (typeof CLASSIFICATION_DIMENSIONS)[number];

// ---------------------------------------------------------------------------
// Option derivation (Req 18.1) — pure
// ---------------------------------------------------------------------------

/**
 * Derive the selectable option lists for every classification dimension from a
 * loaded `DashboardLibraryIndex` (Req 18.1). Each dimension's list is the
 * distinct set of values that actually appear across the document's entries,
 * sorted case-insensitively, with the explicit `"unknown"` marker included when
 * present (Req 18.6).
 *
 * Returns `null` when `index` is `null` — the graceful-absence case where no
 * Library document exists for the run, so the caller hides the facets entirely
 * and the panel renders exactly as today. Pure: the input is never mutated.
 */
export function deriveClassificationOptions(
  index: DashboardLibraryIndex | null,
): ClassificationOptions | null {
  if (index === null) {
    return null;
  }
  const repoType = new Set<string>();
  const lifecycleStatus = new Set<string>();
  const owningTeam = new Set<string>();
  const application = new Set<string>();
  const product = new Set<string>();

  for (const entry of index.repos) {
    repoType.add(entry.repoType);
    lifecycleStatus.add(entry.lifecycleStatus);
    owningTeam.add(entry.owningTeam);
    application.add(entry.application);
    product.add(entry.product);
  }

  return {
    repoType: sortDistinct(repoType),
    lifecycleStatus: sortDistinct(lifecycleStatus),
    owningTeam: sortDistinct(owningTeam),
    application: sortDistinct(application),
    product: sortDistinct(product),
  };
}

/** Sort a value set case-insensitively into a fresh array (deterministic order). */
function sortDistinct(values: Set<string>): string[] {
  return [...values].sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  );
}

// ---------------------------------------------------------------------------
// Filtering (Req 18.1, 18.6) — pure
// ---------------------------------------------------------------------------

/**
 * True when at least one classification dimension carries a selection (i.e. the
 * filter would constrain the list). The empty selection is inactive.
 */
export function isClassificationFilterActive(
  filters: ClassificationFilterState,
): boolean {
  return (
    filters.repoType.length > 0 ||
    filters.lifecycleStatus.length > 0 ||
    filters.owningTeam.length > 0 ||
    filters.application.length > 0 ||
    filters.product.length > 0
  );
}

/**
 * Narrow a summary list by the active classification facets (Req 18.1, 18.6),
 * mirroring the attribute-dimension semantics of `filterSummaries`:
 *
 *   - A dimension with an empty selection is inactive and imposes no constraint;
 *     an empty `ClassificationFilterState` therefore returns every item.
 *   - Within an active dimension the selected values are OR-ed; across active
 *     dimensions the per-dimension results are AND-ed.
 *   - The value compared for each dimension is taken from the summary's joined
 *     `LibraryRepoEntry` (by `repoName` + `scanId`). A summary with no joined
 *     Library entry has no value for any dimension, so — exactly like a `null`
 *     attribute value in `filterSummaries` — it is excluded from any active
 *     dimension. When no dimension is active such a summary is kept.
 *
 * Pure and deterministic: the input array is never mutated, relative order is
 * preserved, and a fresh array is always returned (a slice when no dimension is
 * active), so the no-Library / empty-selection path is a faithful no-op.
 */
export function filterByClassification(
  summaries: RepoSummary[],
  byKey: Map<string, LibraryRepoEntry>,
  filters: ClassificationFilterState,
): RepoSummary[] {
  // No active dimension: impose no constraint (no-op, returns a fresh copy).
  if (!isClassificationFilterActive(filters)) {
    return summaries.slice();
  }
  return summaries.filter((summary) =>
    summaryPassesClassification(summary, byKey, filters),
  );
}

/**
 * True when `summary` satisfies every active classification dimension (AND
 * across dimensions). A summary with no joined Library entry contributes a
 * `null` value to every dimension and so fails any active dimension.
 */
function summaryPassesClassification(
  summary: RepoSummary,
  byKey: Map<string, LibraryRepoEntry>,
  filters: ClassificationFilterState,
): boolean {
  const entry = joinLibraryEntry(summary, byKey);
  if (!dimensionPasses(entry?.repoType ?? null, filters.repoType)) return false;
  if (!dimensionPasses(entry?.lifecycleStatus ?? null, filters.lifecycleStatus)) {
    return false;
  }
  if (!dimensionPasses(entry?.owningTeam ?? null, filters.owningTeam)) {
    return false;
  }
  if (!dimensionPasses(entry?.application ?? null, filters.application)) {
    return false;
  }
  if (!dimensionPasses(entry?.product ?? null, filters.product)) return false;
  return true;
}

/**
 * Membership test for one active classification dimension. An empty `selected`
 * array means the dimension is inactive and imposes no constraint; a `null`
 * value (no joined Library entry) is excluded from any active dimension; else
 * the value must exactly equal one of the selected values (OR within the
 * dimension).
 */
function dimensionPasses(value: string | null, selected: string[]): boolean {
  if (selected.length === 0) return true; // inactive dimension
  if (value === null) return false; // no Library value -> excluded when active
  return selected.includes(value);
}
